import { useState, useEffect, useRef } from "react";

const gold = "#f5a623", blue = "#3b82f6", blue2 = "#60a5fa";
const green = "#22c55e", red = "#ef4444", purple = "#a78bfa";
const navy = "#0b0f1a", navy2 = "#111827", navy3 = "#1a2236", navy4 = "#212d45";
const white = "#ffffff", textCol = "#cbd5e1", muted = "#64748b";
const border = "rgba(255,255,255,0.07)";

function useReveal() {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
}
function Reveal({ children, delay = 0 }) {
  const [ref, vis] = useReveal();
  return (
    <div ref={ref} style={{ opacity: vis ? 1 : 0, transform: vis ? "none" : "translateY(22px)", transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s` }}>
      {children}
    </div>
  );
}

const ss = {
  section: { marginBottom: 72 },
  eyebrow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 12 },
  tag: { fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: blue },
  line: { flex: 1, height: 1, background: border },
  badge: { fontFamily: "monospace", fontSize: "0.68rem", color: muted },
  h2: { fontFamily: "Georgia,serif", fontSize: "clamp(1.5rem,3vw,2.1rem)", fontWeight: 900, color: white, lineHeight: 1.25, marginBottom: 20 },
  prose: { fontSize: "0.97rem", color: textCol, lineHeight: 1.85, marginBottom: 28 },
  hr: { border: "none", borderTop: `1px solid ${border}`, margin: "48px 0" },
};

function Callout({ icon, children, accent = gold }) {
  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${accent}`, borderRadius: 8, padding: "20px 24px", margin: "28px 0", display: "flex", gap: 14, alignItems: "flex-start" }}>
      <span style={{ fontSize: "1.1rem", flexShrink: 0, marginTop: 2 }}>{icon}</span>
      <div style={{ fontSize: "0.88rem", color: textCol, lineHeight: 1.75 }}>{children}</div>
    </div>
  );
}

function Pill({ text, color = gold }) {
  return <span style={{ display: "inline-block", fontSize: "0.75rem", fontWeight: 700, padding: "3px 10px", borderRadius: 4, color, background: `${color}18`, border: `1px solid ${color}33`, marginRight: 4, marginBottom: 4 }}>{text}</span>;
}

// ── S1: MER Breakdown ──
function MERBreakdown() {
  const components = [
    { name: "Management Fee", pct: 1.5, color: gold, inMer: true, desc: "Pays for the portfolio manager's salary, fund company overhead, and profit. The largest component of the MER." },
    { name: "Trailing Commission", pct: 0.75, color: blue2, inMer: true, desc: "Ongoing fee paid from the MER to the advisor/dealer for continuing service — research, advice, and account management. Typically 0.25%–1.0% per year." },
    { name: "Operating Expenses", pct: 0.15, color: purple, inMer: true, desc: "Legal, audit, custodial (Custodian holds the fund assets), and registrar fees (Registrar maintains unitholder records)." },
    { name: "GST / HST on Fees", pct: 0.10, color: green, inMer: true, desc: "Sales taxes applied to the management fee and operating expenses. Added on top of the base fees." },
    { name: "Brokerage Commissions (TER)", pct: 0.08, color: red, inMer: false, desc: "Paid by the fund to brokers when buying/selling securities in the portfolio. NOT in the MER — reported separately as the Trading Expense Ratio (TER). Total cost = MER + TER." },
  ];
  const [active, setActive] = useState(null);
  const merTotal = components.filter(c => c.inMer).reduce((a, c) => a + c.pct, 0).toFixed(2);
  const total = components.reduce((a, c) => a + c.pct, 0).toFixed(2);

  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 24 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "0.95rem", color: white, marginBottom: 6 }}>💸 MER Component Breakdown</div>
        <div style={{ fontFamily: "monospace", fontSize: "0.78rem", color: gold, background: "rgba(245,166,35,.1)", border: "1px solid rgba(245,166,35,.2)", borderRadius: 4, padding: "6px 14px", marginBottom: 18, display: "inline-block" }}>
          MER = (Total Annual Expenses ÷ Average Net Assets) × 100
        </div>
        {/* Stacked bar */}
        <div style={{ height: 26, borderRadius: 6, overflow: "hidden", display: "flex", marginBottom: 16 }}>
          {components.filter(c => c.inMer).map((c, i) => (
            <div key={i} onClick={() => setActive(active === i ? null : i)}
              style={{ width: `${(c.pct / parseFloat(merTotal)) * 100}%`, background: c.color, cursor: "pointer", opacity: active !== null && active !== i ? 0.5 : 1, transition: "opacity 0.2s", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {c.pct >= 0.3 && <span style={{ fontSize: "0.65rem", fontWeight: 700, color: navy }}>{c.pct}%</span>}
            </div>
          ))}
        </div>
        <div style={{ display: "grid", gap: 6, marginBottom: 16 }}>
          {components.map((c, i) => (
            <div key={i} onClick={() => c.inMer ? setActive(active === i ? null : i) : null}
              style={{ background: active === i ? navy4 : navy3, border: `1px solid ${active === i ? c.color + "55" : border}`, borderRadius: 6, padding: "10px 14px", cursor: c.inMer ? "pointer" : "default", transition: "all 0.2s" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 10, height: 10, borderRadius: 2, background: c.color, flexShrink: 0 }} />
                  <span style={{ fontSize: "0.84rem", fontWeight: 600, color: white }}>{c.name}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontFamily: "monospace", fontSize: "0.82rem", color: c.color }}>{c.pct}%</span>
                  <span style={{ fontSize: "0.62rem", fontWeight: 700, padding: "2px 7px", borderRadius: 100, color: c.inMer ? green : red, background: c.inMer ? "rgba(34,197,94,.12)" : "rgba(239,68,68,.12)", border: `1px solid ${c.inMer ? "rgba(34,197,94,.25)" : "rgba(239,68,68,.25)"}` }}>
                    {c.inMer ? "IN MER" : "NOT IN MER"}
                  </span>
                </div>
              </div>
              {active === i && <div style={{ marginTop: 8, fontSize: "0.8rem", color: muted, lineHeight: 1.65, paddingTop: 8, borderTop: `1px solid ${border}` }}>{c.desc}</div>}
            </div>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {[["MER Total (example)", `${merTotal}%`, gold], ["Total Fund Cost (MER + TER)", `${total}%`, blue2]].map(([lbl, val, col]) => (
            <div key={lbl} style={{ background: navy4, border: `1px solid ${col}33`, borderRadius: 8, padding: "12px 16px", textAlign: "center" }}>
              <div style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: col, marginBottom: 4 }}>{lbl}</div>
              <div style={{ fontFamily: "monospace", fontSize: "1.3rem", fontWeight: 700, color: white }}>{val}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── S1b: Loads ──
function LoadsExplorer() {
  const [loadType, setLoadType] = useState("front");
  const configs = {
    front: {
      label: "Front-End Load", icon: "⬆️", color: gold,
      when: "At time of purchase", amount: "Negotiable — 0% to 5%",
      effect: "Reduces the actual amount invested. On a $10,000 investment with 2% load: $200 fee, only $9,800 is invested and working for the client.",
      formula: "Net Investment = Gross Contribution − (Gross × Load %)",
      pill: "Paid to Dealer at Purchase",
    },
    back: {
      label: "Back-End Load (DSC)", icon: "⬇️", color: red,
      when: "At time of redemption (sale)", amount: "Declining scale (e.g., 6% Year 1 → 0% Year 7)",
      effect: "Fee is charged on the original cost, not current value — so gains are protected. Usually charged only if sold before the schedule expires. DSCs were BANNED in Canada effective June 1, 2022.",
      formula: "Redemption Fee = Original Cost × DSC Rate for that Year",
      pill: "Banned in Canada June 2022",
    },
    noload: {
      label: "No-Load Fund", icon: "✅", color: green,
      when: "No fee to buy or sell", amount: "0% sales charge",
      effect: "Dealer is compensated only through the trailing commission embedded in the MER — or not at all for DIY / discount broker funds. Full amount is invested immediately.",
      formula: "Net Investment = Gross Contribution (100% invested)",
      pill: "DIY / Discount Broker",
    },
  };
  const cfg = configs[loadType];

  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "flex", gap: 6, marginBottom: 0 }}>
        {Object.entries(configs).map(([key, val]) => (
          <button key={key} onClick={() => setLoadType(key)} style={{ flex: 1, padding: "10px 10px", background: loadType === key ? navy4 : navy3, border: `1px solid ${loadType === key ? val.color + "55" : border}`, borderBottom: loadType === key ? `2px solid ${val.color}` : `1px solid ${border}`, borderRadius: "6px 6px 0 0", cursor: "pointer", fontFamily: "inherit", fontSize: "0.75rem", fontWeight: 700, color: loadType === key ? val.color : muted, transition: "all 0.2s", whiteSpace: "nowrap" }}>
            {val.icon} {val.label}
          </button>
        ))}
      </div>
      <div style={{ background: navy4, border: `1px solid ${cfg.color}22`, borderRadius: "0 0 8px 8px", padding: 22 }}>
        <div style={{ display: "grid", gap: 8 }}>
          {[["When Paid", cfg.when, blue2], ["Amount", cfg.amount, gold], ["Effect on Investor", cfg.effect, textCol], ["Formula", cfg.formula, purple]].map(([lbl, val, col]) => (
            <div key={lbl} style={{ background: navy3, borderRadius: 6, padding: "10px 14px", display: "flex", gap: 12, flexWrap: "wrap" }}>
              <div style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: col, width: 110, flexShrink: 0, paddingTop: 2 }}>{lbl}</div>
              <div style={{ fontSize: "0.83rem", color: col === textCol ? textCol : textCol, flex: 1, lineHeight: 1.65 }}>{val}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 12 }}><Pill text={cfg.pill} color={cfg.color} /></div>
        {loadType === "back" && (
          <div style={{ marginTop: 12, background: navy3, border: `1px solid ${gold}33`, borderRadius: 6, padding: "12px 16px" }}>
            <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: gold, marginBottom: 8 }}>🚗 DSC Declining Schedule (Example)</div>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {[["Yr 1", "6%", red], ["Yr 2", "5.5%", "#f97316"], ["Yr 3", "5%", "#f97316"], ["Yr 4", "4%", gold], ["Yr 5", "3%", gold], ["Yr 6", "1.5%", blue2], ["Yr 7+", "0%", green]].map(([yr, rate, col], i) => (
                <div key={i} style={{ background: navy4, border: `1px solid ${col}33`, borderRadius: 6, padding: "8px 10px", textAlign: "center", minWidth: 52 }}>
                  <div style={{ fontSize: "0.62rem", color: muted }}>{yr}</div>
                  <div style={{ fontFamily: "monospace", fontSize: "0.82rem", fontWeight: 700, color: col }}>{rate}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 10, fontSize: "0.78rem", color: muted }}>💡 10% Free Ride: Most DSC funds allow withdrawal of 10% of units annually without triggering any DSC fee.</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Calculators: Loads ──
function LoadCalculators() {
  const [frontGross, setFrontGross] = useState(5000);
  const [frontLoad, setFrontLoad] = useState(2.5);
  const [frontResult, setFrontResult] = useState(null);

  const [dscOriginal, setDscOriginal] = useState(10000);
  const [dscCurrent, setDscCurrent] = useState(15000);
  const [dscRate, setDscRate] = useState(4);
  const [dscResult, setDscResult] = useState(null);

  function calcFront() {
    const g = parseFloat(frontGross), l = parseFloat(frontLoad) / 100;
    const fee = g * l;
    const net = g - fee;
    setFrontResult({ g, fee: fee.toFixed(2), net: net.toFixed(2), pct: frontLoad });
  }

  function calcDSC() {
    const orig = parseFloat(dscOriginal), curr = parseFloat(dscCurrent), rate = parseFloat(dscRate) / 100;
    const fee = orig * rate;
    const netRedemption = curr - fee;
    setDscResult({ orig, curr, fee: fee.toFixed(2), netRedemption: netRedemption.toFixed(2), rate: dscRate });
  }

  return (
    <div style={{ display: "grid", gap: 16, margin: "28px 0" }}>
      {/* Front-End */}
      <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 24 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "0.95rem", color: white, marginBottom: 6 }}>🧮 Front-End Load Calculator</div>
        <div style={{ fontFamily: "monospace", fontSize: "0.75rem", color: gold, background: "rgba(245,166,35,.1)", border: "1px solid rgba(245,166,35,.2)", borderRadius: 4, padding: "5px 12px", marginBottom: 14, display: "inline-block" }}>
          Net Investment = Gross − (Gross × Load %)
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
          {[["Gross Contribution ($)", frontGross, setFrontGross], ["Front-End Load (%)", frontLoad, setFrontLoad]].map(([lbl, val, set], i) => (
            <div key={i}>
              <label style={{ display: "block", fontSize: "0.62rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 5 }}>{lbl}</label>
              <input type="number" step="any" value={val} onChange={e => set(e.target.value)}
                style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "10px 12px", color: white, fontFamily: "monospace", fontSize: "0.88rem", outline: "none" }} />
            </div>
          ))}
        </div>
        <button onClick={calcFront} style={{ background: gold, color: navy, border: "none", padding: "10px 22px", fontFamily: "inherit", fontSize: "0.82rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>Calculate →</button>
        {frontResult && (
          <div style={{ background: navy4, border: "1px solid rgba(245,166,35,.2)", borderRadius: 8, padding: 16, marginTop: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
              {[["Gross", `$${parseFloat(frontResult.g).toLocaleString()}`, muted], ["Fee", `-$${frontResult.fee}`, red], ["Net Invested", `$${parseFloat(frontResult.net).toLocaleString()}`, green]].map(([lbl, val, col]) => (
                <div key={lbl} style={{ background: navy3, borderRadius: 6, padding: "10px 10px", textAlign: "center" }}>
                  <div style={{ fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: col, marginBottom: 3 }}>{lbl}</div>
                  <div style={{ fontFamily: "monospace", fontSize: "0.95rem", fontWeight: 700, color: white }}>{val}</div>
                </div>
              ))}
            </div>
            {[`Sales charge: $${frontResult.g.toLocaleString()} × ${frontResult.pct}% = $${frontResult.fee}`, `Net invested: $${frontResult.g.toLocaleString()} − $${frontResult.fee} = $${parseFloat(frontResult.net).toLocaleString()}`].map((s, i) => (
              <div key={i} style={{ fontFamily: "monospace", fontSize: "0.78rem", color: textCol, display: "flex", gap: 8 }}>
                <span style={{ color: gold }}>→</span>{s}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Back-End DSC */}
      <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 24 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "0.95rem", color: white, marginBottom: 6 }}>🧮 Back-End Load (DSC) Calculator</div>
        <div style={{ fontFamily: "monospace", fontSize: "0.75rem", color: gold, background: "rgba(245,166,35,.1)", border: "1px solid rgba(245,166,35,.2)", borderRadius: 4, padding: "5px 12px", marginBottom: 14, display: "inline-block" }}>
          Redemption Fee = Original Cost × DSC Rate
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 12 }}>
          {[["Original Cost ($)", dscOriginal, setDscOriginal], ["Current Value ($)", dscCurrent, setDscCurrent], ["DSC Rate (%)", dscRate, setDscRate]].map(([lbl, val, set], i) => (
            <div key={i}>
              <label style={{ display: "block", fontSize: "0.62rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 5 }}>{lbl}</label>
              <input type="number" step="any" value={val} onChange={e => set(e.target.value)}
                style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "10px 12px", color: white, fontFamily: "monospace", fontSize: "0.88rem", outline: "none" }} />
            </div>
          ))}
        </div>
        <button onClick={calcDSC} style={{ background: gold, color: navy, border: "none", padding: "10px 22px", fontFamily: "inherit", fontSize: "0.82rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>Calculate →</button>
        {dscResult && (
          <div style={{ background: navy4, border: "1px solid rgba(245,166,35,.2)", borderRadius: 8, padding: 16, marginTop: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
              {[["Current Value", `$${parseFloat(dscResult.curr).toLocaleString()}`, blue2], ["DSC Fee", `-$${dscResult.fee}`, red], ["Net Proceeds", `$${parseFloat(dscResult.netRedemption).toLocaleString()}`, green]].map(([lbl, val, col]) => (
                <div key={lbl} style={{ background: navy3, borderRadius: 6, padding: "10px 10px", textAlign: "center" }}>
                  <div style={{ fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: col, marginBottom: 3 }}>{lbl}</div>
                  <div style={{ fontFamily: "monospace", fontSize: "0.95rem", fontWeight: 700, color: white }}>{val}</div>
                </div>
              ))}
            </div>
            {[`DSC charged on original cost: $${parseFloat(dscResult.orig).toLocaleString()} × ${dscResult.rate}% = $${dscResult.fee}`, `Net proceeds: $${parseFloat(dscResult.curr).toLocaleString()} − $${dscResult.fee} = $${parseFloat(dscResult.netRedemption).toLocaleString()}`, `Gain protected: Capital gain is on $${parseFloat(dscResult.curr).toLocaleString()} − $${parseFloat(dscResult.orig).toLocaleString()} = $${(dscResult.curr - dscResult.orig).toLocaleString()} (DSC doesn't touch the gain)`].map((s, i) => (
              <div key={i} style={{ fontFamily: "monospace", fontSize: "0.78rem", color: i === 2 ? gold : textCol, display: "flex", gap: 8, padding: "2px 0" }}>
                <span style={{ color: gold }}>→</span>{s}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── S2: Fund Services ──
function SWPPanel() {
  const [planType, setPlanType] = useState("fixed_dollar");
  const [portfolioValue, setPortfolioValue] = useState(500000);
  const [annualReturn, setAnnualReturn] = useState(5);
  const [withdrawal, setWithdrawal] = useState(2500);
  const [withdrawalPct, setWithdrawalPct] = useState(6);
  const [age, setAge] = useState(65);
  const [lifeExp, setLifeExp] = useState(90);
  const [years, setYears] = useState(20);

  const plans = {
    fixed_dollar: { label: "Fixed Dollar", icon: "💵", color: blue2 },
    fixed_pct: { label: "Fixed Percentage", icon: "📊", color: gold },
    fixed_period: { label: "Fixed Period", icon: "📅", color: purple },
    life_exp: { label: "Life Expectancy", icon: "📈", color: green },
  };

  function getMonthlyWithdrawal() {
    const v = parseFloat(portfolioValue);
    switch (planType) {
      case "fixed_dollar": return parseFloat(withdrawal);
      case "fixed_pct": return (v * parseFloat(withdrawalPct) / 100) / 12;
      case "fixed_period": return v / (parseFloat(years) * 12);
      case "life_exp": return v / ((parseFloat(lifeExp) - parseFloat(age)) * 12);
      default: return 0;
    }
  }

  const monthly = getMonthlyWithdrawal().toFixed(2);
  const annual = (getMonthlyWithdrawal() * 12).toFixed(2);
  const annualRate = parseFloat(annualReturn) / 100;
  const encroachment = parseFloat(annual) > portfolioValue * annualRate;

  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(100px,1fr))", gap: 6, marginBottom: 14 }}>
        {Object.entries(plans).map(([key, val]) => (
          <button key={key} onClick={() => setPlanType(key)} style={{ padding: "12px 8px", background: planType === key ? navy4 : navy3, border: `1px solid ${planType === key ? val.color + "55" : border}`, borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: "0.72rem", fontWeight: 700, color: planType === key ? val.color : muted, transition: "all 0.2s", textAlign: "center" }}>
            <div style={{ fontSize: "1.1rem", marginBottom: 4 }}>{val.icon}</div>
            {val.label}
          </button>
        ))}
      </div>

      <div style={{ background: navy3, border: `1px solid ${plans[planType].color}33`, borderRadius: 8, padding: 24 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "0.95rem", color: white, marginBottom: 14 }}>{plans[planType].icon} {plans[planType].label} Withdrawal Plan</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 10, marginBottom: 16 }}>
          <div>
            <label style={{ display: "block", fontSize: "0.62rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 5 }}>Portfolio Value ($)</label>
            <input type="number" value={portfolioValue} onChange={e => setPortfolioValue(e.target.value)}
              style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "10px 12px", color: white, fontFamily: "monospace", fontSize: "0.88rem", outline: "none" }} />
          </div>
          <div>
            <label style={{ display: "block", fontSize: "0.62rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 5 }}>Annual Return (%)</label>
            <input type="number" step="0.1" value={annualReturn} onChange={e => setAnnualReturn(e.target.value)}
              style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "10px 12px", color: white, fontFamily: "monospace", fontSize: "0.88rem", outline: "none" }} />
          </div>
          {planType === "fixed_dollar" && (
            <div>
              <label style={{ display: "block", fontSize: "0.62rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 5 }}>Monthly Withdrawal ($)</label>
              <input type="number" value={withdrawal} onChange={e => setWithdrawal(e.target.value)}
                style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "10px 12px", color: white, fontFamily: "monospace", fontSize: "0.88rem", outline: "none" }} />
            </div>
          )}
          {planType === "fixed_pct" && (
            <div>
              <label style={{ display: "block", fontSize: "0.62rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 5 }}>Annual Withdrawal (%)</label>
              <input type="number" step="0.1" value={withdrawalPct} onChange={e => setWithdrawalPct(e.target.value)}
                style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "10px 12px", color: white, fontFamily: "monospace", fontSize: "0.88rem", outline: "none" }} />
            </div>
          )}
          {planType === "fixed_period" && (
            <div>
              <label style={{ display: "block", fontSize: "0.62rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 5 }}>Period (Years)</label>
              <input type="number" value={years} onChange={e => setYears(e.target.value)}
                style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "10px 12px", color: white, fontFamily: "monospace", fontSize: "0.88rem", outline: "none" }} />
            </div>
          )}
          {planType === "life_exp" && (<>
            <div>
              <label style={{ display: "block", fontSize: "0.62rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 5 }}>Current Age</label>
              <input type="number" value={age} onChange={e => setAge(e.target.value)}
                style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "10px 12px", color: white, fontFamily: "monospace", fontSize: "0.88rem", outline: "none" }} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "0.62rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 5 }}>Life Expectancy</label>
              <input type="number" value={lifeExp} onChange={e => setLifeExp(e.target.value)}
                style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "10px 12px", color: white, fontFamily: "monospace", fontSize: "0.88rem", outline: "none" }} />
            </div>
          </>)}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
          {[["Monthly Withdrawal", `$${parseFloat(monthly).toLocaleString()}`, plans[planType].color], ["Annual Withdrawal", `$${parseFloat(annual).toLocaleString()}`, plans[planType].color]].map(([lbl, val, col]) => (
            <div key={lbl} style={{ background: navy4, border: `1px solid ${col}33`, borderRadius: 8, padding: "12px 14px", textAlign: "center" }}>
              <div style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: col, marginBottom: 4 }}>{lbl}</div>
              <div style={{ fontFamily: "monospace", fontSize: "1.2rem", fontWeight: 700, color: white }}>{val}</div>
            </div>
          ))}
        </div>
        {planType === "life_exp" && (
          <div style={{ fontFamily: "monospace", fontSize: "0.79rem", color: muted, marginBottom: 10 }}>
            → Formula: $${parseFloat(portfolioValue).toLocaleString()} ÷ ({lifeExp} − {age}) years = ${parseFloat(annual).toLocaleString()} / year
          </div>
        )}
        <div style={{ background: encroachment ? "rgba(239,68,68,.08)" : "rgba(34,197,94,.08)", border: `1px solid ${encroachment ? "rgba(239,68,68,.25)" : "rgba(34,197,94,.25)"}`, borderRadius: 8, padding: "12px 16px" }}>
          <div style={{ fontSize: "0.82rem", fontWeight: 700, color: encroachment ? red : green, marginBottom: 4 }}>
            {encroachment ? "⚠️ Capital Encroachment Risk" : "✅ Sustainable Withdrawal Rate"}
          </div>
          <div style={{ fontSize: "0.8rem", color: textCol, lineHeight: 1.7 }}>
            {encroachment
              ? `Annual withdrawal ($${parseFloat(annual).toLocaleString()}) exceeds portfolio return ($${(portfolioValue * annualRate).toFixed(0).toLocaleString()}). The portfolio is being depleted — capital encroachment. At this rate, principal will erode over time.`
              : `Annual withdrawal ($${parseFloat(annual).toLocaleString()}) is within the fund's expected return ($${(portfolioValue * annualRate).toFixed(0).toLocaleString()}). Portfolio may be self-sustaining.`}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── DCA Calculator ──
function DCACalculator() {
  const [entries, setEntries] = useState([
    { amount: 1000, price: 10 },
    { amount: 1000, price: 8 },
    { amount: 1000, price: 12 },
  ]);
  const [result, setResult] = useState(null);

  function update(i, field, val) {
    const updated = entries.map((e, idx) => idx === i ? { ...e, [field]: parseFloat(val) || 0 } : e);
    setEntries(updated);
    setResult(null);
  }

  function calc() {
    const rows = entries.map(e => ({ ...e, units: e.amount / e.price }));
    const totalInvested = rows.reduce((a, r) => a + r.amount, 0);
    const totalUnits = rows.reduce((a, r) => a + r.units, 0);
    const avgCost = totalInvested / totalUnits;
    const avgPrice = rows.reduce((a, r) => a + r.price, 0) / rows.length;
    setResult({ rows, totalInvested, totalUnits: totalUnits.toFixed(2), avgCost: avgCost.toFixed(2), avgPrice: avgPrice.toFixed(2), saving: (avgPrice - avgCost).toFixed(2) });
  }

  function addRow() { setEntries([...entries, { amount: 1000, price: 10 }]); setResult(null); }

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 24, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "0.95rem", color: white, marginBottom: 6 }}>🧮 Dollar Cost Averaging Calculator</div>
      <div style={{ fontFamily: "monospace", fontSize: "0.75rem", color: gold, background: "rgba(245,166,35,.1)", border: "1px solid rgba(245,166,35,.2)", borderRadius: 4, padding: "5px 12px", marginBottom: 16, display: "inline-block" }}>
        Average Cost = Total Amount Invested ÷ Total Units Purchased
      </div>
      <div style={{ display: "grid", gap: 6, marginBottom: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          {["Investment ($)", "Unit Price ($)", "Units Bought"].map(h => (
            <div key={h} style={{ fontSize: "0.62rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase" }}>{h}</div>
          ))}
        </div>
        {entries.map((e, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, alignItems: "center" }}>
            <input type="number" value={e.amount} onChange={ev => update(i, "amount", ev.target.value)}
              style={{ background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "9px 11px", color: white, fontFamily: "monospace", fontSize: "0.85rem", outline: "none" }} />
            <input type="number" step="0.01" value={e.price} onChange={ev => update(i, "price", ev.target.value)}
              style={{ background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "9px 11px", color: white, fontFamily: "monospace", fontSize: "0.85rem", outline: "none" }} />
            <div style={{ fontFamily: "monospace", fontSize: "0.85rem", color: blue2, padding: "9px 11px", background: navy4, borderRadius: 6 }}>
              {e.price > 0 ? (e.amount / e.price).toFixed(2) : "—"}
            </div>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={calc} style={{ background: gold, color: navy, border: "none", padding: "10px 22px", fontFamily: "inherit", fontSize: "0.82rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>Calculate →</button>
        <button onClick={addRow} style={{ background: "transparent", color: blue2, border: `1px solid rgba(96,165,250,.3)`, padding: "10px 18px", fontFamily: "inherit", fontSize: "0.78rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>+ Add Period</button>
      </div>
      {result && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))", gap: 10, marginBottom: 14 }}>
            {[["Total Invested", `$${result.totalInvested.toLocaleString()}`, blue2], ["Total Units", result.totalUnits, purple], ["Avg Cost/Unit", `$${result.avgCost}`, green], ["Avg Price/Unit", `$${result.avgPrice}`, gold], ["Saving/Unit", `$${result.saving}`, green]].map(([lbl, val, col]) => (
              <div key={lbl} style={{ background: navy4, border: `1px solid ${col}33`, borderRadius: 8, padding: "11px 12px", textAlign: "center" }}>
                <div style={{ fontSize: "0.58rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: col, marginBottom: 3 }}>{lbl}</div>
                <div style={{ fontFamily: "monospace", fontSize: "0.95rem", fontWeight: 700, color: white }}>{val}</div>
              </div>
            ))}
          </div>
          <div style={{ background: navy4, borderRadius: 8, padding: 14 }}>
            {[
              `Total invested: ${result.rows.map(r => `$${r.amount}`).join(" + ")} = $${result.totalInvested.toLocaleString()}`,
              `Total units: ${result.rows.map(r => r.units.toFixed(2)).join(" + ")} = ${result.totalUnits} units`,
              `Average cost: $${result.totalInvested.toLocaleString()} ÷ ${result.totalUnits} = $${result.avgCost}/unit`,
              `Average price: ($${result.rows.map(r => r.price).join(" + $")}) ÷ ${result.rows.length} = $${result.avgPrice}/unit`,
              `DCA benefit: Average cost ($${result.avgCost}) < Average price ($${result.avgPrice}) — saved $${result.saving}/unit`,
            ].map((step, i) => (
              <div key={i} style={{ fontFamily: "monospace", fontSize: "0.78rem", color: i === 4 ? gold : textCol, display: "flex", gap: 8, padding: "2px 0" }}>
                <span style={{ color: gold }}>→</span>{step}
              </div>
            ))}
          </div>
          <Callout icon="💡" accent={green}>
            <strong style={{ color: green }}>Why DCA Works:</strong> When prices are low you automatically buy <em>more units</em> with the same fixed dollar amount. When prices are high you buy <em>fewer units</em>. The result: your average cost per unit is always <strong>lower than the average price</strong> across the same periods — a permanent mathematical advantage of investing fixed amounts regularly.
          </Callout>
        </div>
      )}
    </div>
  );
}

// ── S3: Fund Services Overview ──
function FundServicesPanel() {
  const [active, setActive] = useState(null);
  const services = [
    {
      icon: "💳", name: "Pre-Authorized Chequing (PAC)", color: green,
      aka: "Voluntary Accumulation Plan / Dollar Cost Averaging",
      desc: "Investor authorizes automatic withdrawal of a fixed dollar amount from their bank account at regular intervals (e.g., $200/month) to purchase fund units.",
      benefit: "Builds wealth systematically. Removes the temptation to time the market. Creates DCA benefit — lower average cost per unit over time.",
      bestFor: "Long-term investors in the accumulation phase. Ideal for RRSPs and TFSAs.",
    },
    {
      icon: "💸", name: "Systematic Withdrawal Plan (SWP)", color: blue2,
      aka: "Income Stream in Retirement",
      desc: "Regular automatic payments from the investor's fund holdings — monthly, quarterly, or annually. Four payout structures: Fixed Dollar, Fixed Percentage, Fixed Period, Life Expectancy.",
      benefit: "Provides predictable retirement income without selling the entire position. Flexible amount and frequency.",
      bestFor: "Retirees or investors who need regular income from accumulated savings.",
    },
    {
      icon: "🔄", name: "Systematic Transfer Plan (STP)", color: purple,
      aka: "Automatic Fund-to-Fund Movement",
      desc: "Automatically moves money from one fund to another within the same fund family at regular intervals. Example: moving profits from a Money Market fund into an Equity fund slowly over time.",
      benefit: "Avoids the risk of making a large lump-sum investment at the wrong time. Gradual rebalancing without manual intervention.",
      bestFor: "Investors who want to gradually shift between funds without making timing decisions.",
    },
    {
      icon: "🔀", name: "Switching / Exchange", color: gold,
      aka: "Fund-to-Fund Transfer Within Same Family",
      desc: "Moving money from one fund to another within the same fund family. May trigger a switching fee (though often waived by advisors).",
      benefit: "Allows tactical rebalancing without leaving the fund family. Usually processed at NAVPU.",
      bestFor: "Investors rebalancing after market movements or changing their risk profile.",
    },
  ];

  return (
    <div style={{ margin: "28px 0", display: "grid", gap: 10 }}>
      {services.map((s, i) => (
        <div key={i} onClick={() => setActive(active === i ? null : i)}
          style={{ background: active === i ? navy4 : navy3, border: `1px solid ${active === i ? s.color + "55" : border}`, borderRadius: 8, padding: "16px 20px", cursor: "pointer", transition: "all 0.2s" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: "1.3rem" }}>{s.icon}</span>
              <div>
                <div style={{ fontSize: "0.88rem", fontWeight: 700, color: white }}>{s.name}</div>
                <div style={{ fontSize: "0.72rem", color: s.color, marginTop: 2 }}>{s.aka}</div>
              </div>
            </div>
            <span style={{ color: active === i ? s.color : muted, fontSize: "1rem", transform: active === i ? "rotate(90deg)" : "none", transition: "transform 0.25s", flexShrink: 0 }}>›</span>
          </div>
          {active === i && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${border}` }}>
              {[["How It Works", s.desc, textCol], ["Key Benefit", s.benefit, green], ["Best For", s.bestFor, blue2]].map(([lbl, val, col]) => (
                <div key={lbl} style={{ display: "flex", gap: 12, marginBottom: 8, flexWrap: "wrap" }}>
                  <div style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: s.color, width: 90, flexShrink: 0, paddingTop: 2 }}>{lbl}</div>
                  <div style={{ fontSize: "0.83rem", color: col, flex: 1, lineHeight: 1.65 }}>{val}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── S3: Tax ──
function TaxImplications() {
  return (
    <div style={{ margin: "28px 0", display: "grid", gap: 12 }}>
      {[
        {
          title: "Fee-Based Account Fees → Tax Deductible", color: green, icon: "✅",
          desc: "Fees paid directly by the investor for investment advice in a fee-based account (e.g., 1% annual fee charged by the advisor) are tax-deductible in non-registered accounts. This is a significant tax advantage of fee-based vs. commission-based models.",
          note: "Must be for a non-registered account and for the purpose of earning investment income.",
        },
        {
          title: "Loads (Commissions) → NOT Immediately Deductible", color: gold, icon: "⚠️",
          desc: "Front-end loads are added to the Adjusted Cost Base (ACB) of the investment. This doesn't reduce tax today, but it reduces the capital gain when the investment is eventually sold. Not immediately deductible — the benefit is deferred.",
          note: "ACB = Original purchase price + Loads paid. Lower ACB = Higher capital gain on sale.",
        },
        {
          title: "MERs → Deducted Inside the Fund", color: blue2, icon: "ℹ️",
          desc: "MERs are deducted inside the fund before returns are reported. They reduce the taxable income distributed by the fund. The investor never directly pays the MER — it's already removed before they see the return. Not separately deductible.",
          note: "MER impact is indirect — it reduces the distributed return, which reduces the taxable amount.",
        },
        {
          title: "High-Turnover Funds → Taxable Capital Gain Distributions", color: red, icon: "❌",
          desc: "When a fund manager sells securities at a profit, those gains must be distributed to unitholders — who then owe capital gains tax, even if they reinvested the distribution. In non-registered accounts, high-turnover funds can create unwanted annual tax bills.",
          note: "Avoid high-turnover funds in non-registered accounts. Hold them in registered accounts where capital gains are sheltered.",
        },
      ].map((item, i) => (
        <div key={i} style={{ background: navy3, border: `1px solid ${item.color}33`, borderRadius: 8, padding: "18px 20px" }}>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <span style={{ fontSize: "1.1rem", flexShrink: 0, marginTop: 2 }}>{item.icon}</span>
            <div>
              <div style={{ fontSize: "0.86rem", fontWeight: 700, color: white, marginBottom: 6 }}>{item.title}</div>
              <div style={{ fontSize: "0.82rem", color: textCol, lineHeight: 1.7, marginBottom: 8 }}>{item.desc}</div>
              <div style={{ fontSize: "0.76rem", color: item.color, fontStyle: "italic" }}>📌 {item.note}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Quiz ──
const questions = [
  { q: "Which fee is typically paid by the fund to the dealer/advisor for ongoing client service?", opts: ["Front-End Load", "Trailing Commission", "Switching Fee", "Performance Fee"], correct: 1, fb: { y: "Correct! The trailing commission is paid from the fund's MER to the advisor/dealer as ongoing compensation for continued client service — annual advice, account reviews, and support.", n: "Not quite. The trailing commission (inside the MER) is the ongoing annual payment from the fund to the advisor for continuous service. Front-end loads are one-time purchase fees." } },
  { q: "Dollar Cost Averaging results in a lower average cost per unit because:", opts: ["You buy fewer units when prices are low", "You buy more units when prices are low and fewer when prices are high", "The dealer waives the commission", "Prices always go up over time"], correct: 1, fb: { y: "Correct! With a fixed dollar investment, low prices mean more units purchased; high prices mean fewer units purchased. This asymmetry means the average cost per unit will always be below the average price.", n: "Not quite. DCA works because a fixed dollar buys MORE units when cheap and FEWER when expensive — automatically overweighting purchases at low prices. Average cost < average price." } },
  { q: "If an investor withdraws money from a fund faster than the fund is generating returns, this is called:", opts: ["Capital Encroachment", "Dollar Cost Averaging", "Compounding", "Accrual Basis Accounting"], correct: 0, fb: { y: "Correct! Capital encroachment occurs when withdrawals exceed the fund's return — the investor is dipping into their original capital. If sustained, the portfolio will be fully depleted before the investor intended.", n: "Not quite. Capital encroachment = withdrawals exceeding returns, slowly consuming principal. This is a critical risk in retirement planning — withdrawal rate must be carefully matched to expected return." } },
  { q: "A 'Front-End Load' is deducted from:", opts: ["The fund's NAV per unit", "The investor's gross contribution at the time of purchase", "The redemption proceeds when selling", "The fund manager's salary"], correct: 1, fb: { y: "Correct! A front-end load is charged at purchase — deducted directly from the amount the investor contributes. If you invest $10,000 with a 2% front-end load, only $9,800 is actually invested.", n: "Not quite. Front-end load = deducted from gross contribution at time of PURCHASE. Back-end load (DSC) = deducted from redemption proceeds at time of SALE." } },
  { q: "Which expense is NOT included in the MER?", opts: ["Legal fees", "Audit fees", "Brokerage commissions (trading costs)", "Trailing commissions"], correct: 2, fb: { y: "Correct! Brokerage commissions paid to trade securities in the portfolio are NOT part of the MER — they are reported separately as the Trading Expense Ratio (TER). Total fund cost = MER + TER.", n: "Not quite. Brokerage commissions are excluded from the MER and reported as the TER. Legal, audit, trailing commissions, and taxes ARE all included in the MER." } },
  { q: "The '10% Free Ride' feature typically refers to:", opts: ["The ability to switch funds for free once per year", "The ability to redeem 10% of units annually without a DSC fee", "A 10% bonus on annual deposits", "A full waiver of the MER for one year"], correct: 1, fb: { y: "Correct! Most DSC fund agreements allow investors to redeem up to 10% of their units annually without triggering the deferred sales charge — providing some liquidity before the DSC schedule expires.", n: "Not quite. The 10% free ride = redeem up to 10% of units per year from a DSC fund without paying the deferred sales charge. It provides partial liquidity within an otherwise illiquid DSC structure." } },
  { q: "Systematic Withdrawal Plans (SWP) are best suited for:", opts: ["Investors in the accumulation phase building wealth", "Investors needing regular income in retirement", "Speculators seeking high-risk returns", "Investors buying a house within 1 year"], correct: 1, fb: { y: "Correct! SWPs are designed for the decumulation (distribution) phase — typically retirement. They allow investors to draw regular income from their accumulated savings in a structured, automatic way.", n: "Not quite. SWPs serve investors who need regular income from existing savings — typically retirees. PAC (Pre-Authorized Chequing) is the accumulation equivalent — for building wealth." } },
  { q: "Under a 'Fixed Period' withdrawal plan, the amount withdrawn each period:", opts: ["Is fixed at a set dollar amount every month", "Fluctuates based on remaining value and time left", "Is always exactly 10% of the portfolio", "Is tax-free under all circumstances"], correct: 1, fb: { y: "Correct! In a fixed-period plan, the formula adjusts withdrawals to deplete the fund exactly at the end of the specified period — so if the fund grows more, withdrawals increase; if less, they decrease.", n: "Not quite. Fixed Period = the formula adjusts the withdrawal to exactly exhaust the portfolio by the end of the period. Fixed Dollar = same amount every period regardless of portfolio value." } },
  { q: "A 'Switching Fee' may be charged when:", opts: ["An investor moves money from one fund to another within the same fund family", "An investor buys a fund for the first time", "The fund manager changes the portfolio holdings", "The MER increases above a threshold"], correct: 0, fb: { y: "Correct! A switching fee can apply when an investor moves money between funds within the same family. Though often waived by advisors as a goodwill gesture, it is a permissible fee.", n: "Not quite. A switching fee is charged for fund-to-fund transfers within a fund family (e.g., moving from a bond fund to an equity fund at the same company). Often waived but technically permitted." } },
  { q: "Pre-Authorized Chequing (PAC) plans are an example of:", opts: ["A Systematic Withdrawal Plan", "A Voluntary Accumulation Plan", "A Hedge Fund strategy", "A Closed-End Fund feature"], correct: 1, fb: { y: "Correct! PAC is a voluntary accumulation plan — the investor voluntarily authorizes regular automatic contributions to build wealth systematically over time, combined with the dollar cost averaging benefit.", n: "Not quite. PAC is a Voluntary Accumulation Plan (building wealth in). SWP is the reverse — drawing income out. They are opposite stages of the investment lifecycle." } },
];

function Quiz() {
  const [current, setCurrent] = useState(0);
  const [answered, setAnswered] = useState({});
  const [done, setDone] = useState(false);
  const score = Object.values(answered).filter(a => a.correct).length;
  const total = questions.length;
  function answer(idx) { if (answered[current] !== undefined) return; setAnswered({ ...answered, [current]: { chosen: idx, correct: idx === questions[current].correct } }); }
  function next() { if (current < total - 1) setCurrent(current + 1); else setDone(true); }
  function retry() { setCurrent(0); setAnswered({}); setDone(false); }
  const q = questions[current], ans = answered[current];
  const pct = Math.round(score / total * 100);
  return (
    <div style={{ background: navy2, border: `1px solid ${border}`, borderRadius: 12, padding: 36, marginTop: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "1.3rem", color: white }}>Chapter 16 Quiz</div>
        <div style={{ fontFamily: "monospace", fontSize: "0.78rem", color: muted }}>Question {current + 1} of {total}</div>
      </div>
      <div style={{ height: 4, background: navy4, borderRadius: 2, marginBottom: 28, overflow: "hidden" }}>
        <div style={{ height: "100%", background: `linear-gradient(90deg,${gold},${blue})`, borderRadius: 2, width: `${(current + 1) / total * 100}%`, transition: "width 0.4s ease" }} />
      </div>
      {!done ? (
        <>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.05rem", color: white, lineHeight: 1.55, marginBottom: 22 }}>{q.q}</div>
          <div style={{ display: "grid", gap: 10 }}>
            {q.opts.map((opt, i) => {
              const isChosen = ans?.chosen === i, isCorrect = i === q.correct;
              let bg = navy3, bdr = border, col = textCol;
              if (ans) { if (isCorrect) { bg = "rgba(34,197,94,.08)"; bdr = green; col = "#4ade80"; } else if (isChosen) { bg = "rgba(239,68,68,.08)"; bdr = "#ef4444"; col = "#f87171"; } }
              return (
                <button key={i} onClick={() => answer(i)} disabled={!!ans}
                  style={{ background: bg, border: `1px solid ${bdr}`, borderRadius: 8, padding: "14px 18px", textAlign: "left", fontFamily: "inherit", fontSize: "0.88rem", color: col, cursor: ans ? "default" : "pointer", transition: "all 0.2s", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ width: 26, height: 26, flexShrink: 0, background: navy4, border: `1px solid ${border}`, borderRadius: 5, display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: "monospace", fontSize: "0.72rem", color: muted }}>{["A","B","C","D"][i]}</span>
                  {opt}
                </button>
              );
            })}
          </div>
          {ans && (
            <>
              <div style={{ marginTop: 14, padding: "13px 16px", borderRadius: 7, fontSize: "0.84rem", lineHeight: 1.65, background: ans.correct ? "rgba(34,197,94,.07)" : "rgba(239,68,68,.07)", border: `1px solid ${ans.correct ? "rgba(34,197,94,.25)" : "rgba(239,68,68,.2)"}`, color: ans.correct ? "#86efac" : "#fca5a5" }}>
                {ans.correct ? "✓ " : "✗ "}{ans.correct ? q.fb.y : q.fb.n}
              </div>
              <button onClick={next} style={{ marginTop: 20, background: gold, color: navy, border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>
                {current < total - 1 ? "Next Question →" : "See My Results →"}
              </button>
            </>
          )}
        </>
      ) : (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ width: 110, height: 110, borderRadius: "50%", margin: "0 auto 20px", background: `conic-gradient(${gold} 0% ${pct}%, ${navy4} ${pct}%)`, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
            <div style={{ position: "absolute", inset: 14, borderRadius: "50%", background: navy2 }} />
            <span style={{ position: "relative", fontFamily: "Georgia,serif", fontSize: "1.5rem", fontWeight: 900, color: gold, zIndex: 1 }}>{score}/{total}</span>
          </div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.5rem", color: white, marginBottom: 8 }}>
            {score === total ? "Perfect Score! 🏆" : score >= 8 ? "Strong Result ✦" : score >= 6 ? "Good Effort" : "Keep Studying"}
          </div>
          <div style={{ fontSize: "0.9rem", color: muted, marginBottom: 24 }}>
            {score === total ? "Flawless. You've mastered mutual fund fees and services." : score >= 8 ? "Excellent — review any missed questions." : score >= 6 ? "Good start. Revisit MER components, loads, and DCA mechanics." : "Focus on MER vs TER, load types, capital encroachment, and DCA benefits."}
          </div>
          <button onClick={retry} style={{ background: "transparent", color: gold, border: `1px solid rgba(245,166,35,.35)`, padding: "10px 26px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>↺ Try Again</button>
        </div>
      )}
    </div>
  );
}

// ── Glossary ──
const glossary = [
  { term: "MER (Management Expense Ratio)", def: "Total annual cost of running a fund as % of average net assets — includes management fee, trailing commission, operating expenses, and taxes. Deducted before returns are shown." },
  { term: "Management Fee", def: "Largest MER component. Pays the portfolio manager and fund company. Does not include trailing commission." },
  { term: "Trailing Commission", def: "Annual fee paid from MER to the advisor/dealer for ongoing client service. Typically 0.25%–1.0% of assets per year." },
  { term: "TER (Trading Expense Ratio)", def: "Brokerage commissions paid to trade securities inside the fund. NOT included in MER. Total fund cost = MER + TER." },
  { term: "Front-End Load", def: "Sales charge deducted from investor's gross contribution at time of purchase. Negotiable from 0%–5%. Reduces amount actually invested." },
  { term: "Back-End Load (DSC)", def: "Deferred Sales Charge — fee paid when redeeming units early, based on a declining schedule. Charged on original cost. BANNED in Canada effective June 1, 2022." },
  { term: "10% Free Ride", def: "DSC fund feature allowing redemption of up to 10% of units annually without triggering the deferred sales charge." },
  { term: "No-Load Fund", def: "No sales charge to buy or sell. Dealer compensated only via trailing commission embedded in MER (or not at all for DIY funds)." },
  { term: "Adjusted Cost Base (ACB)", def: "Original purchase price plus any loads paid. Used to calculate capital gain on sale. Loads add to ACB, reducing future capital gains." },
  { term: "Pre-Authorized Chequing (PAC)", def: "Automatic fixed-dollar investments at regular intervals from bank account. A Voluntary Accumulation Plan. Creates Dollar Cost Averaging benefit." },
  { term: "Dollar Cost Averaging (DCA)", def: "Investing fixed amounts regularly — buying more units when cheap, fewer when expensive. Average cost per unit < average price per unit." },
  { term: "Systematic Withdrawal Plan (SWP)", def: "Regular automatic payments from fund holdings. Four types: Fixed Dollar, Fixed Percentage, Fixed Period, Life Expectancy." },
  { term: "Capital Encroachment", def: "When withdrawals exceed the fund's return — the investor consumes principal. If sustained, the portfolio will eventually be depleted." },
  { term: "Fixed Dollar SWP", def: "Withdraw the same dollar amount each period. Units sold varies depending on current NAVPU." },
  { term: "Fixed Period SWP", def: "Withdrawals are calculated to exactly deplete the portfolio over a specified number of years. Amount adjusts based on remaining value." },
  { term: "Life Expectancy SWP", def: "Annual withdrawal = Portfolio Value ÷ (Life Expectancy − Current Age). Designed to last the investor's projected remaining lifetime." },
  { term: "Systematic Transfer Plan (STP)", def: "Automatic movement of money from one fund to another within the same family at regular intervals." },
  { term: "Switching Fee", def: "Fee charged for transferring between funds within the same fund family. Often waived by advisors, but legally permissible." },
  { term: "Fee-Based Account", def: "Advisory model where investor pays a direct annual fee for advice. Fee is tax-deductible in non-registered accounts." },
  { term: "High-Turnover Fund (Tax)", def: "Funds that trade frequently distribute capital gains annually — taxable in non-registered accounts even if the investor didn't sell their units." },
];

// ── Main Export ──
export default function Chapter16() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const fn = () => {
      const h = document.documentElement;
      setProgress((h.scrollTop || document.body.scrollTop) / (h.scrollHeight - h.clientHeight) * 100);
    };
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const tocItems = [
    ["01", "MER — Components, Breakdown & Stacked Visualizer"],
    ["02", "Loads — Front-End, Back-End (DSC) & No-Load"],
    ["03", "Fund Services — PAC, SWP, STP & Switching"],
    ["04", "SWP Types — Fixed Dollar, %, Period & Life Expectancy"],
    ["05", "Tax Implications of Fees"],
    ["🧮", "Front-End Load, DSC & DCA Calculators"],
    ["✦", "Knowledge Check Quiz"],
    ["✦", "Key Terms Glossary"],
  ];

  return (
    <div style={{ background: navy, minHeight: "100vh", fontFamily: "'Nunito Sans', sans-serif" }}>
      <div style={{ position: "fixed", top: 0, left: 0, height: 3, background: `linear-gradient(90deg,${gold},${blue})`, width: `${progress}%`, zIndex: 9999, transition: "width 0.15s ease" }} />
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 24px 80px" }}>

        <Reveal>
          <div style={{ padding: "80px 0 56px", borderBottom: `1px solid ${border}`, marginBottom: 56 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(245,166,35,.12)", border: "1px solid rgba(245,166,35,.25)", color: gold, fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", padding: "5px 14px", borderRadius: 100, marginBottom: 24 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: gold, display: "inline-block" }} />
              IFC/CSC · Chapter 16
            </div>
            <h1 style={{ fontFamily: "Georgia,serif", fontSize: "clamp(2.2rem,5vw,3.4rem)", fontWeight: 900, color: white, lineHeight: 1.15, marginBottom: 20 }}>
              Mutual Fund Fees, <span style={{ color: gold }}>Services &amp; Tax</span>
            </h1>
            <p style={{ fontSize: "1.05rem", color: muted, maxWidth: 600, lineHeight: 1.8, marginBottom: 32 }}>
              MER components, load types, systematic plans (PAC/SWP/STP), dollar cost averaging, capital encroachment, and the tax treatment of fund fees — with interactive calculators for every formula.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 24, paddingTop: 24, borderTop: `1px solid ${border}` }}>
              {[["📖","5","Core Topics"],["⏱","~20 min","read"],["🎯","10","practice questions"],["🧮","3","calculators"],["📊","3","interactive tools"]].map(([icon,val,lbl],i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: blue }}>{icon}</span>
                  <span style={{ fontSize: "0.82rem", color: muted }}><strong style={{ color: textCol }}>{val}</strong> {lbl}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div style={{ background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${gold}`, borderRadius: 8, padding: "28px 32px", marginBottom: 56 }}>
            <div style={{ fontFamily: "Georgia,serif", fontSize: "0.85rem", fontWeight: 700, color: gold, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 16 }}>📋 In This Chapter</div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {tocItems.map(([num, label], i) => (
                <li key={i} style={{ padding: "6px 0", borderBottom: i < tocItems.length - 1 ? `1px solid ${border}` : "none", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontFamily: "monospace", fontSize: "0.7rem", color: muted, width: 24, flexShrink: 0 }}>{num}</span>
                  <span style={{ color: textCol, fontSize: "0.9rem", fontWeight: 600 }}>{label}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        {/* S1 MER */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 01</span><span style={ss.badge}>MER & TER</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Management Expense Ratio (MER)</h2>
            <p style={ss.prose}>
              The MER is deducted from the fund <span style={{ color: gold, fontWeight: 600 }}>before returns are shown</span> to the client — so investors never write a cheque for it, but they pay it every single year. Click each component in the breakdown to see who gets it and why:
            </p>
          </Reveal>
          <Reveal delay={0.1}><MERBreakdown /></Reveal>
          <Reveal delay={0.2}>
            <Callout icon="⚠️" accent={red}>
              <strong style={{ color: red }}>MER vs TER:</strong> The MER covers fund management, trailing commissions, operating expenses and taxes. Brokerage commissions from trading the portfolio are <strong>excluded from the MER</strong> and reported separately as the <strong>Trading Expense Ratio (TER)</strong>. The true all-in cost is MER + TER.
            </Callout>
          </Reveal>
        </div>

        <hr style={ss.hr} />

        {/* S2 Loads */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 02</span><span style={ss.badge}>Sales Loads</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Load Structures</h2>
            <p style={ss.prose}>
              Loads are paid by the investor <span style={{ color: gold, fontWeight: 600 }}>directly to the dealer</span> — not to the fund. They are separate from the MER. Three structures to know for the exam:
            </p>
          </Reveal>
          <Reveal delay={0.1}><LoadsExplorer /></Reveal>
          <Reveal delay={0.2}><LoadCalculators /></Reveal>
          <Reveal delay={0.3}>
            <Callout icon="🚫" accent={red}>
              <strong style={{ color: red }}>DSC Ban (June 1, 2022):</strong> Deferred Sales Charges were officially banned in Canada on June 1, 2022. Older DSC schedules on existing holdings may still be winding down — but no new DSC funds can be sold. The 10% free-ride and declining schedule are still exam-tested knowledge for legacy holdings.
            </Callout>
          </Reveal>
        </div>

        <hr style={ss.hr} />

        {/* S3 Services */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 03</span><span style={ss.badge}>Fund Services</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Mutual Fund Services</h2>
            <p style={ss.prose}>
              Beyond buying and selling, mutual funds offer a suite of <span style={{ color: gold, fontWeight: 600 }}>automatic services</span> that help investors accumulate wealth, draw income, and transfer between funds without manual action. Click each to explore:
            </p>
          </Reveal>
          <Reveal delay={0.1}><FundServicesPanel /></Reveal>
        </div>

        <hr style={ss.hr} />

        {/* S4 SWP + DCA */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 04</span><span style={ss.badge}>SWP & DCA</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>SWP Types &amp; Dollar Cost Averaging</h2>
            <p style={ss.prose}>
              Two calculators — the SWP planner shows all four withdrawal structures with a live capital encroachment warning, and the DCA calculator proves why average cost always beats average price:
            </p>
          </Reveal>
          <Reveal delay={0.1}><SWPPanel /></Reveal>
          <Reveal delay={0.2}><DCACalculator /></Reveal>
        </div>

        <hr style={ss.hr} />

        {/* S5 Tax */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 05</span><span style={ss.badge}>Tax Treatment</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Tax Implications of Fund Fees</h2>
            <p style={ss.prose}>
              Not all fees are treated equally by the CRA. The account type and how the fee is structured determines whether it's <span style={{ color: green, fontWeight: 600 }}>deductible</span>, <span style={{ color: gold, fontWeight: 600 }}>deferred</span>, or <span style={{ color: red, fontWeight: 600 }}>invisible but still costly</span>:
            </p>
          </Reveal>
          <Reveal delay={0.1}><TaxImplications /></Reveal>
        </div>

        <hr style={ss.hr} />

        {/* Quiz */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Knowledge Check</span><span style={ss.badge}>10 Questions</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Test Your Understanding</h2>
            <p style={ss.prose}>10 questions covering MER components, load types, DCA mechanics, SWP structures, capital encroachment, and fee tax treatment from Chapter 16.</p>
          </Reveal>
          <Reveal delay={0.1}><Quiz /></Reveal>
        </div>

        <hr style={ss.hr} />

        {/* Glossary */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Quick Reference</span><span style={ss.badge}>Key Terms</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Glossary</h2>
            <p style={ss.prose}>All essential terms from Chapter 16 — fees, loads, fund services, and tax treatment.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 10, margin: "28px 0" }}>
              {glossary.map((g, i) => (
                <div key={i} style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: "18px 20px", transition: "border-color 0.2s", cursor: "default" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(245,166,35,.3)"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = border}>
                  <div style={{ fontFamily: "monospace", fontSize: "0.72rem", color: gold, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>{g.term}</div>
                  <div style={{ fontSize: "0.83rem", color: muted, lineHeight: 1.65 }}>{g.def}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

      </div>
    </div>
  );
}
